import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  CheckCircle, XCircle, MessageSquare, Clock, User, Building2, 
  Hammer, AlertTriangle, ChevronDown, Filter, Search, RefreshCw,
  Pause, Play, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminApprovals = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [filterRole, setFilterRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showRequestInfoModal, setShowRequestInfoModal] = useState(null);
  const [requestInfoMessage, setRequestInfoMessage] = useState('');
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, suspended: 0 });

  const isSuperAdmin = user?.role === 'super_admin';

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        status: activeTab,
        ...(filterRole !== 'all' && { role: filterRole }),
        ...(searchQuery && { search: searchQuery })
      });
      
      const response = await fetch(`${API}/api/admin/approvals?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApprovals(data.users || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0, suspended: 0 });
      }
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, filterRole, searchQuery]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprovalAction = async (userId, action, message = null) => {
    try {
      setActionLoading(userId);
      const response = await fetch(`${API}/api/admin/approvals/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, message })
      });
      
      if (response.ok) {
        fetchApprovals();
        setShowRequestInfoModal(null);
        setRequestInfoMessage('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Action failed');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const tabs = [
    { id: 'pending', label: 'Pending', count: stats.pending, color: 'bg-amber-100 text-amber-700' },
    { id: 'approved', label: 'Approved', count: stats.approved, color: 'bg-green-100 text-green-700' },
    { id: 'rejected', label: 'Rejected', count: stats.rejected, color: 'bg-red-100 text-red-700' },
    { id: 'suspended', label: 'Suspended', count: stats.suspended, color: 'bg-neutral-100 text-neutral-700' },
  ];

  const getRoleIcon = (role) => {
    switch (role) {
      case 'sponsor': return <Building2 className="w-4 h-4" />;
      case 'contractor': return <Hammer className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      suspended: 'bg-neutral-200 text-neutral-700',
      info_requested: 'bg-blue-100 text-blue-700'
    };
    return (
      <Badge className={`${styles[status] || styles.pending} font-medium`}>
        {status === 'info_requested' ? 'Info Requested' : status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
            Approval Management
          </h1>
          <p className="text-neutral-500 mt-1">Review and manage sponsor & contractor applications</p>
        </div>
        <Button 
          onClick={fetchApprovals} 
          variant="outline" 
          className="gap-2"
          data-testid="refresh-approvals-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-neutral-200 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#FF3B30] text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeTab === tab.id ? 'bg-white/20 text-white' : tab.color
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-approvals"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-4 py-2 border border-neutral-200 rounded-lg bg-white"
          data-testid="filter-role"
        >
          <option value="all">All Roles</option>
          <option value="sponsor">Sponsors Only</option>
          <option value="contractor">Contractors Only</option>
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
        </div>
      ) : approvals.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-neutral-200">
          <Clock className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No {activeTab} applications found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((item) => (
            <div 
              key={item.user_id} 
              className="bg-white rounded-xl border border-neutral-200 p-6 hover:shadow-md transition-shadow"
              data-testid={`approval-item-${item.user_id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* User Info */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                    {getRoleIcon(item.role)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-neutral-900">{item.name}</h3>
                      {getStatusBadge(item.approval_status)}
                    </div>
                    <p className="text-sm text-neutral-500">{item.email}</p>
                    <div className="flex items-center gap-3 mt-2 text-sm text-neutral-500">
                      <span className="capitalize flex items-center gap-1">
                        {getRoleIcon(item.role)} {item.role}
                      </span>
                      {item.company_name && (
                        <span>• {item.company_name}</span>
                      )}
                      <span>• Applied {new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                    {item.admin_notes && (
                      <p className="mt-2 text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-lg inline-block">
                        <MessageSquare className="w-3 h-3 inline mr-1" />
                        {item.admin_notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {activeTab === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleApprovalAction(item.user_id, 'approve')}
                        disabled={actionLoading === item.user_id}
                        className="bg-green-600 hover:bg-green-700 text-white gap-1"
                        data-testid={`approve-btn-${item.user_id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleApprovalAction(item.user_id, 'reject')}
                        disabled={actionLoading === item.user_id}
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                        data-testid={`reject-btn-${item.user_id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => setShowRequestInfoModal(item)}
                        disabled={actionLoading === item.user_id}
                        variant="outline"
                        className="gap-1"
                        data-testid={`request-info-btn-${item.user_id}`}
                      >
                        <Mail className="w-4 h-4" />
                        Request Info
                      </Button>
                    </>
                  )}
                  
                  {activeTab === 'approved' && (
                    <Button
                      onClick={() => handleApprovalAction(item.user_id, 'suspend')}
                      disabled={actionLoading === item.user_id}
                      variant="outline"
                      className="text-amber-600 border-amber-200 hover:bg-amber-50 gap-1"
                      data-testid={`suspend-btn-${item.user_id}`}
                    >
                      <Pause className="w-4 h-4" />
                      Suspend
                    </Button>
                  )}

                  {activeTab === 'suspended' && (
                    <Button
                      onClick={() => handleApprovalAction(item.user_id, 'unsuspend')}
                      disabled={actionLoading === item.user_id}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1"
                      data-testid={`unsuspend-btn-${item.user_id}`}
                    >
                      <Play className="w-4 h-4" />
                      Unsuspend
                    </Button>
                  )}

                  {activeTab === 'rejected' && (
                    <Button
                      onClick={() => handleApprovalAction(item.user_id, 'approve')}
                      disabled={actionLoading === item.user_id}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1"
                      data-testid={`reapprove-btn-${item.user_id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Re-approve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Request Info Modal */}
      {showRequestInfoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Request More Information</h3>
            <p className="text-sm text-neutral-500 mb-4">
              Send a message to {showRequestInfoModal.name} requesting additional information.
            </p>
            <textarea
              value={requestInfoMessage}
              onChange={(e) => setRequestInfoMessage(e.target.value)}
              placeholder="Please provide additional details about your business license..."
              className="w-full p-3 border border-neutral-200 rounded-lg resize-none h-32"
              data-testid="request-info-message"
            />
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowRequestInfoModal(null);
                  setRequestInfoMessage('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleApprovalAction(showRequestInfoModal.user_id, 'request_info', requestInfoMessage)}
                disabled={!requestInfoMessage.trim()}
                className="flex-1 bg-[#FF3B30] hover:bg-[#DC2626]"
                data-testid="send-request-info-btn"
              >
                Send Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApprovals;
